import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { CommonPaths } from '@utils/path'

import { NameDto } from '@model/NameDto'

import { Loading } from '@shared/components/Loading/Loading'
import { AlreadyUploadedModal } from '@shared/modals/AlreadyUploaded.modal'

import {
  DocumentManagementNonNCIForm,
  DocumentManagementNonNCIFormValues,
} from './DocumentManagementNonNCIForm'

export const useDocumentManagementNonNCIFetch = (
  documentCategoryId?: number,
  documentCategoryTypeId?: number
) => {
  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.document.categories(documentCategoryId, locale),
    queryFn: () => apiClient.getDocumentCategories(documentCategoryId!, locale),
    enabled: !!documentCategoryId,
  })

  const documentCategoryType = data?.types.find((x) => x.id === documentCategoryTypeId)

  return {
    data,
    documentId: documentCategoryTypeId,
    documentCategoryTypeName: documentCategoryType?.name ?? '',
    isLoading,
  }
}

export const useDocumentManagementNonNCIMutate = () => {
  const navigate = useNavigate()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [isLoading, setLoading] = useState(false)
  const alreadyUploadedModal = useModal(AlreadyUploadedModal)

  const mutate = async (values: DocumentManagementNonNCIFormValues) => {
    try {
      setLoading(true)
      await apiClient.uploadDocuments({ documents: values.documents })
      await refreshAccount(locale)

      const documentListKey = queryKeys.document.list({ locale }, locale)
      const generalStatusKey = queryKeys.document.generalStatus(account?.id, locale)

      queryClient.removeQueries({
        queryKey: documentListKey,
        exact: false,
      })

      queryClient.removeQueries({
        queryKey: generalStatusKey,
        exact: false,
      })

      addSnackbar.success({
        message: t('Profile.Documents submitted and will be reviewed within 1 workday'),
        ignoreMessageLength: true,
      })
      navigate(CommonPaths.DocumentManagement)
    } catch (error: unknown) {
      alreadyUploadedModal.open({ message: (error as any).response?.response?.data?.code })
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  return {
    mutate,
    isLoading,
  }
}

interface DocumentManagementNonNCIProps {
  documentTitle: string
  showTerms?: boolean
}

export const DocumentManagementNonNCIPage: React.FC<DocumentManagementNonNCIProps> = (props) => {
  const { documentTitle, showTerms = true } = props

  const navigate = useNavigate()

  const { t } = useTranslation()
  const { refreshAccount } = useAccountWriteContext()
  const locale = useSessionLanguage()

  const location = useLocation()

  const [values] = useState<{
    documentCategory: NameDto
    documentCategoryType: NameDto
  }>(location.state)

  const { documentCategoryTypeName, documentId } = useDocumentManagementNonNCIFetch(
    values?.documentCategory.id,
    values?.documentCategoryType.id
  )

  const mutation = useDocumentManagementNonNCIMutate()

  useEffect(() => {
    if (!values) {
      navigate(CommonPaths.DocumentManagement)
    }
  }, [navigate, values])

  const handleSubmit = async (values: DocumentManagementNonNCIFormValues) => {
    try {
      await mutation.mutate(values)
      await refreshAccount(locale)
    } catch (e: unknown) {
      console.error(e)
      throw e
    }
  }

  return (
    <React.Fragment>
      {!mutation.isLoading && values && (
        <DocumentManagementNonNCIForm
          documentId={documentId}
          documentTitle={documentTitle}
          documentCategoryTypeName={documentCategoryTypeName}
          documentCategory={values.documentCategory}
          documentCategoryType={values.documentCategoryType}
          showTerms={showTerms}
          onSubmit={handleSubmit}
        />
      )}
      {mutation.isLoading && (
        <Loading isLoading={mutation.isLoading} showLoadingIcon text={t('Wallet.Submitting')} />
      )}
    </React.Fragment>
  )
}
